import { ResponsiveDialog } from '@cibo/ui'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
} from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { Select } from 'formik-mui'
import { prop } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgreement, useCreateAttachment } from '../queries'
import { AttachmentFileDescriptor, AttachmentFileDescriptors } from '../types'
import { FileUploadFormValues, UploadFileForm } from './UploadFileForm'

interface AddAttachmentModalProps {
  agreementId: string
  onComplete?(): void
  onError?(error: any): void
  onRequestClose(): void
  open: boolean
}

const ModalFormResetter = ({ open }: { open: boolean }) => {
  const { resetForm } = useFormikContext()
  useEffect(() => {
    if (open) {
      resetForm()
    }
  }, [open])
  return null
}

export const AddAttachmentModal = ({
  agreementId,
  onRequestClose,
  open,
}: AddAttachmentModalProps) => {
  const { t } = useTranslation('impact/Agreements/Attachments')

  const agreement = useAgreement(agreementId)

  const fileDescriptors =
    agreement.data?.agreementType && AttachmentFileDescriptors[agreement.data?.agreementType]

  const { mutateAsync: createAttachment, isLoading, isError, reset } = useCreateAttachment(
    agreementId
  )

  useEffect(() => reset(), [open])

  const handleUpload = async (formValues: {
    fileUpload: FileUploadFormValues
    descriptor: string
  }) => {
    createAttachment({
      description: formValues.descriptor,
      file: formValues.fileUpload.file.file,
      fileName: formValues.fileUpload.file.name,
    })
      .then(() => {
        onRequestClose()
      })
      .catch((error: Error) => {
        console.log(error)
      })
  }

  return (
    <Formik onSubmit={handleUpload} initialValues={{ descriptor: '' } as any}>
      {({ isValid, submitForm, resetForm, values }) => {
        return (
          <>
            <ModalFormResetter open={open} />

            <ResponsiveDialog open={open} onClose={onRequestClose}>
              <DialogTitle>{t('modalTitle')}</DialogTitle>

              <DialogContent>
                <Form>
                  {!!fileDescriptors && (
                    <FormControl fullWidth>
                      <FormLabel>{t('uploadType')}</FormLabel>

                      <Field component={Select} name="descriptor">
                        {fileDescriptors?.map(prop('description')).map((description: string) => (
                          <MenuItem key={description} value={description}>
                            {t(`attachmentType_${description}`)}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  )}

                  <UploadFileForm
                    uploadError={isError ? t('uploadProblem') : undefined}
                    fileDescriptor={fileDescriptors?.find(
                      ({ description }: AttachmentFileDescriptor) =>
                        description === values.descriptor
                    )}
                  />
                </Form>
              </DialogContent>

              <DialogActions>
                <Button onClick={onRequestClose} variant="text" style={{ marginRight: 32 }}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  variant="contained"
                  loading={isLoading}
                  onClick={submitForm}
                  disabled={!isValid}
                  data-testid="submit_uploaded_file"
                >
                  {t('submit')}
                </LoadingButton>
              </DialogActions>
            </ResponsiveDialog>
          </>
        )
      }}
    </Formik>
  )
}
